import type { OrderFormValues } from '@/components/ls-order-form/LsOrderForm';
import type { CartItem } from '@/features/cart/cart.store';
import { api } from '@/shared/api/client';
import type { Order } from '@/shared/api/schemas';

interface Params {
  partnerSlug: string;
  items: CartItem[];
  values: OrderFormValues;
}

/** Сборка тела заказа. Вынесена из экрана: тестируется без React. */
export function buildOrderPayload({ partnerSlug, items, values }: Params) {
  return {
    partner: partnerSlug,
    items: items.map((item) => ({
      product_id: item.productId,
      color: item.color,
      size: item.size,
      qty: item.qty,
      // Сторона печати едет в заказ: без неё выбор на экране ничего
      // не значит, а печатник не знает, перёд это или спина.
      print_side: item.printSide ?? 'front',
      // Своя сборка: сервер сверяет цвет с палитрой конструктора, а не
      // с цветами товара каталога (белой там нет).
      is_custom: item.isCustom ?? false,
      // Два файла позиции: вещь с принтом и сам принт. Без них заказ
      // приходит одним текстом, и производству нечего печатать —
      // ровно это и было.
      preview_url: item.previewUrl ?? null,
      print_url: item.printUrl ?? null,
      placements: item.placements.map((placement) => ({
        print_id: placement.printId,
        area: placement.area,
        x: placement.x,
        y: placement.y,
        width: placement.width,
        height: placement.height,
        rotation: placement.rotation,
        flipped: placement.flipped ?? false,
      })),
    })),
    customer: {
      full_name: values.fullName,
      phone: values.phone,
      email: values.email,
      // Самовывоз — адреса нет: забирают у нас.
      address: values.deliveryMethod === 'pickup' ? null : values.address,
      comment: buildComment(values.comment),
    },
    delivery_method: values.deliveryMethod ?? 'russia',
    consent: true,
    form_trap: values.trap,
    form_ms: values.elapsedMs,
  };
}

export function buildComment(comment: string): string | null {
  const trimmed = comment.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export async function submitOrder(params: Params): Promise<Order> {
  // Ключ создаётся на попытку: повторный клик не создаст второй заказ
  return api.createOrder(params.partnerSlug, buildOrderPayload(params), crypto.randomUUID());
}
